import React, {useCallback, useContext, useRef, useState} from 'react';
import getConfig from 'next/config';

import Boomerang from '~/src/components/svg/general/Boomerang';
import Link from '~/src/components/link/Link';

import {TeaserLayoutProps} from '~/src/components/teaser/layout/default/TeaserLayoutDefault';

import {SiteArea} from '~/src/utilities/constants/navigation';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {classNames} from '~/src/utilities/helpers/css';
import {WemfTypes} from '~/src/utilities/constants/tracking';
import {sendPixel} from '~/src/utilities/tracking/netmetrix';
import {Tenant} from '~/src/utilities/constants/tenant';
import {getCategoryUrl} from '~/src/utilities/helpers/link';
import {createNavigationTracker} from '~/src/utilities/tracking/gtm';
import initPolyglot from '~/src/utilities/libs/polyglot';

import css from './TeaserLayoutCarousel.module.scss';
import i18nStrings from './TeaserLayoutCarousel.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const {publicRuntimeConfig: config} = getConfig();

interface TeaserLayoutCarouselProps extends TeaserLayoutProps {
    options?: {
        showLead?: boolean;
        title?: string;
        type?: string;
    };
}

const TeaserLayoutCarousel = ({
    layout,
    title,
    showTitle,
    colorScheme,
    carouselType,
    pointerCategoryID,
    fullUrlPath,
    children,
    options
}: TeaserLayoutCarouselProps) => {
    const {siteArea} = useContext(SettingsContext);

    const [state, setState] = useState({
        showLeftArrow: false,
        showRightArrow: true
    });

    const teasers = useRef<HTMLDivElement>(null);

    const navigate = useCallback((scrollDirection: string) => {
        const teasersElement = teasers.current;

        if (teasersElement) {
            const elementOffset = 10;
            const scrollOffset = teasersElement.offsetWidth > 974 ? 10 : 0;
            const elementWidth = teasersElement.offsetWidth + elementOffset + scrollOffset;
            const elementScrollWidth = teasersElement.scrollWidth;
            const maxScrollPosition = elementScrollWidth - elementWidth;
            const currentScrollPos = teasersElement.scrollLeft;
            let shouldTrack = false;

            let newScrollPos;

            if (scrollDirection === 'left') {
                if (currentScrollPos - elementWidth > 0) {
                    newScrollPos = currentScrollPos - elementWidth - elementOffset - scrollOffset;
                    shouldTrack = true;
                } else {
                    newScrollPos = 0;
                }
            } else {
                if (currentScrollPos + elementWidth <= maxScrollPosition) {
                    newScrollPos = currentScrollPos + elementWidth + scrollOffset;
                    shouldTrack = true;
                } else {
                    newScrollPos = elementScrollWidth - elementWidth + elementOffset;
                }
            }

            if (teasersElement.scrollTo) {
                teasersElement.scrollTo({
                    left: newScrollPos,
                    top: 0,
                    behavior: 'smooth'
                });
            } else {
                teasersElement.scrollLeft = newScrollPos;
            }

            setState({
                showLeftArrow: newScrollPos !== 0,
                showRightArrow: newScrollPos !== elementScrollWidth - elementWidth + elementOffset
            });

            if (shouldTrack && carouselType === 'categorySlider') {
                sendPixel(WemfTypes.VIDEO_SLIDER);
            }
        }
    }, []);

    const headerClasses = classNames(css, 'titleHeader', layout, siteArea, colorScheme);
    const carouselClasses = classNames(css, 'teasersWrapper', carouselType, layout, siteArea);

    return (
        <React.Fragment>
            {options?.title && siteArea === SiteArea.NEWS && (
                <div className={headerClasses}>
                    <h1 className={css.titleCarousel}>
                        {fullUrlPath ? (
                            <Link
                                className={css.titleLink}
                                href={getCategoryUrl(fullUrlPath)}
                                onClick={createNavigationTracker(4, options?.title, pointerCategoryID)}
                            >
                                {options?.title}
                                <Boomerang />
                            </Link>
                        ) : (
                            options?.title
                        )}
                    </h1>
                </div>
            )}

            {siteArea === SiteArea.VIDEO && (
                <div className={headerClasses}>
                    {showTitle && (
                        <h1 className={css.title}>
                            {fullUrlPath ? (
                                <Link
                                    className={css.titleLink}
                                    href={getCategoryUrl(fullUrlPath)}
                                    onClick={createNavigationTracker(4, title, pointerCategoryID)}
                                >
                                    {title}
                                    <Boomerang />
                                </Link>
                            ) : (
                                title
                            )}
                        </h1>
                    )}
                </div>
            )}

            <div className={carouselClasses}>
                <div className={css.teasers} ref={teasers}>
                    {children}
                </div>
                {children.length > 4 && (
                    <React.Fragment>
                        {state.showLeftArrow && (
                            <div className={`${css.arrowButton} ${css.arrowLeft}`} onClick={() => navigate('left')}>
                                <Boomerang />
                            </div>
                        )}
                        {state.showRightArrow && (
                            <div className={`${css.arrowButton} ${css.arrowRight}`} onClick={() => navigate('right')}>
                                <Boomerang />
                            </div>
                        )}
                    </React.Fragment>
                )}
            </div>

            {(config.tenantId === Tenant.TWENTY_MIN_DE || config.tenantId === Tenant.TWENTY_MIN_FR) && siteArea === SiteArea.NEWS && (
                <Link className={css.moreVideos} href={getCategoryUrl(fullUrlPath)}>
                    {options?.type === 'article' ? <span>{getTranslation('moreArticles')}</span> : <span>{getTranslation('moreVideos')}</span>}
                    <Boomerang />
                </Link>
            )}
        </React.Fragment>
    );
};

export default TeaserLayoutCarousel;
